import React from 'react';
import { BookOpen, CheckCircle, Circle, HelpCircle, Split, XCircle } from 'lucide-react';
import { QUESTION_TYPES, REVIEW_STATUS } from '../../../constants';
import { useParams } from '../../../hooks';
import { getReviewQuestionUrl } from '../../../utils/urlHelpers';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../ui/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  alternativeRows,
  batchDrawerAlternatives,
  buildDrawerQuestionNumbers,
  buildDrawerSectionRows,
  buildDrawerSubQuestionRows,
  collectDrawerChoiceGroups,
} from '../../../utils/drawerRows';

const Section = (props) => {
  const { answerStatuses, closeDrawer, assignmentQuestionSections, assignmentQuestions } = props;
  const { studentAssignmentId, questionId } = useParams();
  const navigate = useNavigate();

  const rcSubQuestionIds = React.useMemo(() => {
    const ids = new Set();
    assignmentQuestions?.forEach((question) => {
      if (question.type === QUESTION_TYPES.READING_COMPREHENSION) {
        question.subQuestions?.forEach((subQuestionId) => ids.add(subQuestionId));
      }
    });
    return ids;
  }, [assignmentQuestions]);


  const groupedQuestions = React.useMemo(() => {
    if (!answerStatuses) return [];

    const questionIndexMap = answerStatuses.reduce((acc, question, index) => {
      acc[question.questionId] = index + 1;
      return acc;
    }, {});

    const isInSection = (section, responseQuestionId) => {
      if (section.questions.includes(responseQuestionId)) {
        return true;
      }

      return assignmentQuestions?.some(
        (question) =>
          question.type === QUESTION_TYPES.READING_COMPREHENSION &&
          section.questions.includes(question.id) &&
          question.subQuestions?.includes(responseQuestionId)
      );
    };

    const mapSectionQuestions = (section) =>
      answerStatuses
        .filter((question) => isInSection(section, question.questionId))
        .map((question) => ({
          ...question,
          globalIndex: questionIndexMap[question.questionId]
        }));

    if (!assignmentQuestionSections?.length) {
      return [
        {
          id: 'default',
          name: 'Section 1',
          questions: answerStatuses.map((question, index) => ({
            ...question,
            globalIndex: index + 1
          }))
        }
      ];
    }

    return assignmentQuestionSections
      .map((section) => ({
        ...section,
        questions: mapSectionQuestions(section)
      }))
      .filter((section) => section.questions.length > 0);
  }, [answerStatuses, assignmentQuestionSections, assignmentQuestions]);

  const handleQuestionClick = (clickedQuestionId) => {
    if (questionId !== clickedQuestionId) {
      navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: clickedQuestionId }));
      closeDrawer();
    }
  };

  const getQuestionCellStyle = (status, isCurrent) => {
    if (isCurrent) return 'border-primary bg-primary/5 ring-2 ring-primary/20';
    if (status === REVIEW_STATUS.CORRECT) return 'border-green-200 bg-green-50';
    if (status === REVIEW_STATUS.PARTIAL) return 'border-amber-200 bg-amber-50';
    if (status === REVIEW_STATUS.INCORRECT) return 'border-red-200 bg-red-50';
    if (status === REVIEW_STATUS.SKIPPED) return 'border-yellow-200 bg-yellow-50';
    return 'border-border hover:border-primary/30';
  };

  const getQuestionIcon = (status, isCurrent) => {
    if (status === REVIEW_STATUS.CORRECT) return <CheckCircle className="h-3.5 w-3.5 text-green-600" />;
    if (status === REVIEW_STATUS.PARTIAL) return <HelpCircle className="h-3.5 w-3.5 text-amber-600" />;
    if (status === REVIEW_STATUS.INCORRECT) return <XCircle className="h-3.5 w-3.5 text-red-500" />;
    if (status === REVIEW_STATUS.SKIPPED) return <Circle className="h-3.5 w-3.5 text-yellow-600" />;
    if (isCurrent) return <Circle className="h-3.5 w-3.5 fill-current text-primary" />;
    return <Circle className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const allChoiceGroups = React.useMemo(
    () => collectDrawerChoiceGroups(assignmentQuestionSections),
    [assignmentQuestionSections]
  );

  const displayNumbers = React.useMemo(
    () =>
      buildDrawerQuestionNumbers({
        sections: assignmentQuestionSections,
        assignmentQuestions,
        responses: answerStatuses,
      }),
    [assignmentQuestionSections, assignmentQuestions, answerStatuses]
  );

  const numberFor = (id, fallback) => displayNumbers[String(id)] ?? String(fallback ?? '');

  /** The groups a passage's own questions choose among. */
  const subQuestionGroupsFor = (passageId) =>
    allChoiceGroups.filter((group) => String(group.parentQuestionId) === String(passageId));

  /** One question's cell, shared by the loose grid and a choice group's own grid. */
  const renderQuestionCell = ({ questionId: id, status, globalIndex }) => {
    const isCurrent = id === questionId;
    return (
      <Button
        key={id}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleQuestionClick(id)}
        className={cn(
          'relative flex h-14 flex-col items-center justify-center gap-1 p-2',
          getQuestionCellStyle(status, isCurrent)
        )}
      >
        {getQuestionIcon(status, isCurrent)}
        <span className="text-xs font-medium">Q{numberFor(id, globalIndex)}</span>
      </Button>
    );
  };

  /**
   * An "attempt any X of these Y" block. In review the useful count is how many of the
   * student's answers actually counted, not how many they attempted - a group where they
   * answered all three still only earns marks for the best two.
   *
   * A rule offering passages lists each as its whole block. A passage counted when any of its
   * questions did: a displaced passage counts none of them.
   */
  const renderChoiceGroup = ({
    group,
    members,
    alternatives,
    gridClass = 'grid grid-cols-3 gap-2',
    cell,
  }) => {
    // A rule among a passage's own questions only ever offers questions.
    const choices = alternatives || members.map((item) => ({ type: 'question', item }));
    const isGroupActive = choices.some(
      (choice) => choice.type === 'question' && choice.item.questionId === questionId
    );
    const countedCount = choices.filter((choice) =>
      alternativeRows(choice).some((member) => member.countedInChoiceGroup)
    ).length;

    return (
      <div
        key={group.id}
        className={cn(
          'rounded-lg border-2 p-3',
          isGroupActive ? 'border-primary/40 bg-primary/5' : 'border-purple-300 bg-purple-50/40'
        )}
      >
        <div className="mb-2.5 flex items-center gap-1.5">
          <Split className="h-3.5 w-3.5 flex-shrink-0 text-purple-600" />
          <span className="text-xs font-semibold text-purple-800">
            Attempt any {group.requiredCount} of {choices.length}
          </span>
          <span className="ml-auto whitespace-nowrap text-[11px] text-muted-foreground">
            {countedCount}/{group.requiredCount} counted
          </span>
        </div>
        <div className="space-y-2">
          {batchDrawerAlternatives(choices).map((part, partIndex) =>
            part.type === 'rc' ? (
              renderRcBlock(part)
            ) : (
              <div key={`alternatives-${partIndex}`} className={gridClass}>
                {part.items.map(cell || renderQuestionCell)}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  /** A reading-comprehension passage and its questions. */
  const renderRcBlock = ({ parent, subQuestions: rcSubs }) => {
    const isGroupActive = rcSubs.some((sq) => sq.questionId === questionId);
    const correctCount = rcSubs.filter((sq) => sq.status === REVIEW_STATUS.CORRECT).length;
    const totalSubQ = rcSubs.length;

    // A rule offering the whole passage draws its own box around this block, so only a choice
    // among the passage's own questions is labelled here.
    const insideGroups = subQuestionGroupsFor(parent.id);
    const [insideGroup] = insideGroups;
    const choiceLabel =
      insideGroups.length > 1
        ? `${insideGroups.length} choices below`
        : insideGroup
          ? `Any ${insideGroup.requiredCount} of ${(insideGroup.questions || []).length} below`
          : null;

    const subCell = ({ questionId: id, status, globalIndex }) => {
      const isCurrentSub = id === questionId;
      return (
        <Button
          key={id}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleQuestionClick(id)}
          className={cn(
            'flex h-11 flex-col items-center justify-center gap-0.5 p-1',
            getQuestionCellStyle(status, isCurrentSub)
          )}
        >
          {getQuestionIcon(status, isCurrentSub)}
          <span className="text-xs font-medium leading-none">{numberFor(id, globalIndex)}</span>
        </Button>
      );
    };

    return (
      <div
        key={parent.id}
        className={cn(
          'rounded-lg border p-3',
          isGroupActive ? 'border-primary/40 bg-primary/5' : 'border-indigo-200 bg-indigo-50/40'
        )}
      >
        <div className="mb-2.5 flex items-start gap-2">
          <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-indigo-800">
                Q{numberFor(parent.id, rcSubs[0]?.globalIndex)}
              </span>
              <Badge className="h-4 bg-indigo-100 px-1.5 py-0 text-xs text-indigo-700">
                Reading Comprehension
              </Badge>
              {choiceLabel && (
                <Badge className="h-4 bg-purple-100 px-1.5 py-0 text-xs text-purple-700">
                  {choiceLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="mb-2.5 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-green-500 transition-all"
              style={{
                width: totalSubQ > 0 ? `${(correctCount / totalSubQ) * 100}%` : '0%'
              }}
            />
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {correctCount}/{totalSubQ} correct
          </span>
        </div>

        <div className="space-y-2">
          {buildDrawerSubQuestionRows({
            parent,
            rows: rcSubs,
            allChoiceGroups,
          }).map((subRow, subIndex) =>
            subRow.type === 'choiceGroup' ? (
              renderChoiceGroup({
                ...subRow,
                gridClass: 'grid grid-cols-4 gap-1.5',
                cell: subCell,
              })
            ) : (
              <div key={`subs-${subIndex}`} className="grid grid-cols-4 gap-1.5">
                {subRow.items.map(subCell)}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2 space-y-5">
      {groupedQuestions.map((section) => {
        const sectionRows = buildDrawerSectionRows({
          section,
          rows: section.questions,
          assignmentQuestions,
        });

        return (
          <div key={section.id}>
            <h3 className="mb-2.5 flex items-center gap-2 text-sm font-medium text-foreground">
              {section.name}
              <span className="text-xs font-normal text-muted-foreground">
                ({section.questions.length}{' '}
                {section.questions.length === 1 ? 'question' : 'questions'})
              </span>
            </h3>

            <div className="space-y-2.5">
              {/* One pass in paper order, so a block appears where its first question sits
                  rather than after every loose question. */}
              {sectionRows.map((row, rowIndex) => {
                if (row.type === 'choiceGroup') return renderChoiceGroup(row);
                if (row.type === 'rc') return renderRcBlock(row);
                return (
                  <div key={`questions-${rowIndex}`} className="grid grid-cols-3 gap-2">
                    {row.items.map(renderQuestionCell)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { Section };
